/**
 * Azure AD (Entra ID) only authentication context.
 * Handles Azure MSAL: login, logout, refresh, applyTokenSet (to Azure storage only).
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { TokenSet } from "./storage";
import { storage } from "./storage";
import {
  InteractionStatus,
  type AccountInfo,
  type AuthenticationResult,
} from "@azure/msal-browser";
import {
  MsalProvider,
  useMsal,
  useIsAuthenticated,
} from "@azure/msal-react";
import { getMsalInstance, getAzureLoginScopes } from "./azureConfig";
import { getEnv } from "../config/getEnv";
import {
  clearAzureCookiesViaBackend,
  syncAzureTokenCookies,
} from "../services/backend/azureAuth";
import { setBackend401Recovery } from "../services/backend/backend401Recovery";

type User = Record<string, unknown> | null;

function accountToUser(
  account: AccountInfo | null,
): Record<string, unknown> | null {
  if (!account) return null;
  return {
    name: account.name ?? undefined,
    email: account.username,
    user_id: account.localAccountId,
    sub: account.localAccountId,
    ...account,
  };
}

export type AzureAuthContextValue = {
  isAuthenticated: boolean;
  /** In-memory token from MSAL. Backend `api` uses HttpOnly cookies, not this field. */
  accessToken?: string;
  idToken?: string;
  user: User;
  isUserLoading: boolean;
  login: (interactive?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  /** Apply token set to Azure context/storage only. */
  applyTokenSet: (tokenSet: TokenSet) => void;
  logoutAppOnly: () => void;
};

export const AzureAuthContext = createContext<
  AzureAuthContextValue | undefined
>(undefined);

function AzureAuthContextInner({ children }: { children: React.ReactNode }) {
  const { instance, inProgress, accounts } = useMsal();
  const azureSignedIn = useIsAuthenticated();
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [msalInitialized, setMsalInitialized] = useState(false);
  const [azureAccessToken, setAzureAccessToken] = useState<string | undefined>(
    undefined,
  );
  const [idToken, setIdToken] = useState<string | undefined>(undefined);
  const [tokenError, setTokenError] = useState<boolean>(false);

  const scopes = useMemo(() => getAzureLoginScopes(), []);

  // MSAL must be initialized before it can read cache (accounts). Call once when inside provider.
  useEffect(() => {
    let cancelled = false;
    instance
      .initialize()
      .then(() => {
        if (!cancelled) setMsalInitialized(true);
      })
      .catch(() => {
        if (!cancelled) setMsalInitialized(true);
      });
    return () => {
      cancelled = true;
    };
  }, [instance]);

  useEffect(() => {
    if (instance && !account && msalInitialized) {
      setAccount(instance.getActiveAccount());
    }
  }, [instance, account, msalInitialized]);

  const isAuthenticated = azureSignedIn || storage.hasAzureSession();

  const acquireAccessToken = useCallback(async () => {
    if (!accounts.length) return;
    try {
      instance.setActiveAccount(accounts?.[0] ?? null);
      const result: AuthenticationResult = await instance.acquireTokenSilent({
        scopes,
        account: account ?? undefined,
      });
      setAzureAccessToken(result.accessToken);
      setIdToken(result.idToken ?? undefined);
      setTokenError(false);
      const beUrl = getEnv("REACT_APP_BE_URL");
      if (beUrl) {
        try {
          await syncAzureTokenCookies({
            access_token: result.accessToken,
            id_token: result.idToken ?? undefined,
          });
        } catch {
          /* cookie sync failed; API calls may 401 until retry */
        }
      }
      storage.saveAzureAuthSession({
        backendSessionCookie: Boolean(beUrl),
        backendCookieExpiresAt:
          beUrl && result.expiresOn
            ? result.expiresOn.getTime()
            : undefined,
        tokenSet: beUrl
          ? {
              id_token: result.idToken ?? undefined,
              issued_at: Date.now(),
            }
          : {
              access_token: result.accessToken,
              id_token: result.idToken ?? undefined,
              issued_at: Date.now(),
            },
        account: account
          ? {
              username: account.username,
              localAccountId: account.localAccountId,
              name: account.name,
            }
          : undefined,
        issuedAt: Date.now(),
      });
      return result.accessToken;
    } catch (error) {
      setAzureAccessToken(undefined);
      setIdToken(undefined);
      storage.clearAzureAuthSession();
      setTokenError(true);
      return undefined;
    }
  }, [instance, accounts, scopes, account]);

  useEffect(() => {
    if (!azureSignedIn || !accounts.length) {
      setAzureAccessToken(undefined);
      setIdToken(undefined);
      return;
    }
    // Signed in with accounts: acquire token so we can clear isUserLoading
    if (accounts.length) {
      acquireAccessToken();
    }
  }, [azureSignedIn, accounts, acquireAccessToken]);

  const isUserLoading =
    !msalInitialized ||
    inProgress !== InteractionStatus.None ||
    (azureSignedIn && !azureAccessToken && !tokenError);

  const login = useCallback(
    async (interactive: boolean = true) => {
      if (inProgress !== InteractionStatus.None) {
        return;
      }
      try {
        await instance.loginRedirect({ scopes });
      } catch (e) {
        console.error("Azure AD login error", e);
      }
    },
    [instance, scopes, inProgress],
  );

  const logout = useCallback(async () => {
    storage.clearAzureAuthSession();
    if (getEnv("REACT_APP_BE_URL")) {
      clearAzureCookiesViaBackend().catch(() => {});
    }
    try {
      await instance.logoutRedirect();
    } catch (e) {
      console.error("Azure AD logout error", e);
    }
  }, [instance]);

  const logoutAppOnly = useCallback(() => {
    setAzureAccessToken(undefined);
    setIdToken(undefined);
    storage.clearAzureAuthSession();
    if (getEnv("REACT_APP_BE_URL")) {
      clearAzureCookiesViaBackend().catch(() => {});
    }
    try {
      instance.logoutRedirect();
    } catch {
      // ignore
    }
  }, [instance]);

  const refresh = useCallback(async () => {
    await acquireAccessToken();
  }, [acquireAccessToken]);

  useEffect(() => {
    if (!getEnv("REACT_APP_BE_URL")) {
      setBackend401Recovery(null);
      return;
    }
    setBackend401Recovery(async () => {
      try {
        const token = await acquireAccessToken();
        if (token) {
          return true;
        }
      } catch {
        /* ignore */
      }
      return false;
    });
    return () => setBackend401Recovery(null);
  }, [acquireAccessToken]);

  /** Apply token set: sync cookies first when backend URL is set; do not persist access_token to storage then. */
  const applyTokenSet = useCallback((tokenSet: TokenSet) => {
    const beUrl = getEnv("REACT_APP_BE_URL");
    if (beUrl && tokenSet.access_token) {
      void syncAzureTokenCookies({
        access_token: tokenSet.access_token,
        id_token: tokenSet.id_token,
      });
    }
    const currentAzure = storage.readAzureAuthSession();
    if (beUrl) {
      let backendCookieExpiresAt: number | undefined;
      if (tokenSet.expires_in != null && Number.isFinite(tokenSet.expires_in)) {
        backendCookieExpiresAt = Date.now() + Number(tokenSet.expires_in) * 1000;
      }
      storage.saveAzureAuthSession({
        ...(currentAzure ?? {}),
        backendSessionCookie: true,
        backendCookieExpiresAt,
        tokenSet: {
          id_token: tokenSet.id_token,
          issued_at: tokenSet.issued_at ?? Date.now(),
          expires_in: tokenSet.expires_in,
        },
        issuedAt: Date.now(),
      });
    } else {
      storage.saveAzureAuthSession({ ...(currentAzure ?? {}), tokenSet });
    }
  }, []);

  const user = useMemo(() => accountToUser(account ?? null), [account]);

  const value = useMemo<AzureAuthContextValue>(
    () => ({
      isAuthenticated,
      accessToken: azureAccessToken,
      idToken,
      user,
      isUserLoading,
      login,
      logout,
      refresh,
      applyTokenSet,
      logoutAppOnly,
    }),
    [
      isAuthenticated,
      azureAccessToken,
      idToken,
      user,
      isUserLoading,
      login,
      logout,
      refresh,
      applyTokenSet,
      logoutAppOnly,
    ],
  );

  return (
    <AzureAuthContext.Provider value={value}>
      {children}
    </AzureAuthContext.Provider>
  );
}

function AzureAuthFallback({ children }: { children: React.ReactNode }) {
  const value = useMemo<AzureAuthContextValue>(
    () => ({
      isAuthenticated: false,
      user: null,
      isUserLoading: false,
      login: async () => {
        console.error(
          "Azure AD is not configured. Set REACT_APP_AZURE_CLIENT_ID and REACT_APP_AZURE_REDIRECT_URI.",
        );
      },
      logout: async () => {},
      refresh: async () => {},
      applyTokenSet: () => {},
      logoutAppOnly: () => {},
    }),
    [],
  );
  return (
    <AzureAuthContext.Provider value={value}>
      {children}
    </AzureAuthContext.Provider>
  );
}

export function AzureAuthProvider({ children }: { children: React.ReactNode }) {
  const instance = getMsalInstance();

  if (!instance) {
    return <AzureAuthFallback>{children}</AzureAuthFallback>;
  }
  // Always mount MsalProvider when we have an instance so that /azure/callback
  // (AzureCallbackHandler) runs inside the provider and useMsal() gets the real instance.
  // Initialization and loading state are handled inside AzureAuthContextInner.
  return (
    <MsalProvider instance={instance}>
      <AzureAuthContextInner>{children}</AzureAuthContextInner>
    </MsalProvider>
  );
}

export function useAzureAuth(): AzureAuthContextValue {
  const ctx = useContext(AzureAuthContext);
  if (!ctx)
    throw new Error("useAzureAuth must be used within AzureAuthProvider");
  return ctx;
}

export function useOptionalAzureAuth(): AzureAuthContextValue | undefined {
  return useContext(AzureAuthContext);
}
